
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

export type OrderStatus =
	| "pending"
	| "received"
	| "processing"
	| "shipped"
	| "delivered"
	| "cancelled";

export type PayoutStatus = 
  | "pending"
  | "completed"
  | "failed"
  | "pending_account";

export interface OrderTracking {
	id: string;
	order_id: string;
	status: OrderStatus;
	tracking_number?: string;
	carrier?: string;
	notes?: string;
	created_at: string;
}

export interface OrderItem {
	id: string;
	product_id: string;
	name: string;
	price: number;
	quantity: number;
	caseQuantity?: number;
	image?: string;
	status: OrderStatus;
}

export interface Order {
	id: string;
	user_id: string;
	supplier_id: string;
	amount: number;
	items: OrderItem[];
	shipping_address: any;
	status: OrderStatus;
	created_at: string;
	updated_at: string;
	tracking?: OrderTracking[];
  payment_intent_id?: string;
  payout_status?: PayoutStatus;
}

export const useOrders = () => {
	const { user, isSupplier } = useAuth();
	const queryClient = useQueryClient();
	const [isUpdating, setIsUpdating] = useState(false);

	// Fetch orders based on user type
	const fetchOrders = async () => {
		if (!user) return [];

		let query = supabase.from("orders").select(`
        *,
        tracking:order_tracking(*)
      `);

		// Filter by user type
		if (isSupplier) {
			query = query.eq("supplier_id", user.id);
		} else {
			query = query.eq("user_id", user.id);
		}

		const { data, error } = await query.order("created_at", {
			ascending: false,
		});

		if (error) {
			console.error("Error fetching orders:", error);
			throw error;
		}

		return data || [];
	};

	// Fetch a single order by ID
	const fetchOrderById = async (orderId: string) => {
		if (!user || !orderId) return null;

		const { data, error } = await supabase
			.from("orders")
			.select(
				`
        *,
        tracking:order_tracking(*)
      `
			)
			.eq("id", orderId)
			.single();

		if (error) {
			console.error(`Error fetching order ${orderId}:`, error);
			throw error;
		}

		return data;
	};

	// Fetch order items with product details by order ID
	const fetchOrderItemsById = async (orderId: string) => {
		if (!user || !orderId) return [];

		try {
			// Get order items with product details
			const { data: orderItems, error: itemsError } = await supabase
				.from("order_items")
				.select(
					`
          id,
          order_id,
          product_id,
          quantity,
          price,
          status,
          products:product_id (
            name,
            case_quantity,
            product_images (
              image_url,
              is_primary
            )
          )
        `
				)
				.eq("order_id", orderId);

			if (itemsError) {
				console.error(
					`Error fetching order items for ${orderId}:`,
					itemsError
				);
				throw itemsError;
			}

			// Format items to match our interface
			const formattedItems: OrderItem[] = orderItems.map((item) => {
				// Find primary image or use first available
                const productImages = item.products?.product_images || [];
                const primaryImage = productImages && Array.isArray(productImages) ? 
                    productImages.find(img => img.is_primary) || productImages[0] : undefined;

				return {
					id: item.id,
					product_id: item.product_id,
					name: item.products?.name || "Unknown Product",
					price: parseFloat(item.price.toString()),
					quantity: item.quantity,
					caseQuantity: item.products?.case_quantity || 1,
					image: primaryImage?.image_url,
					status: item.status as OrderStatus,
				};
			});

			return formattedItems;
		} catch (error) {
			console.error(
				`Error processing order items for ${orderId}:`,
				error
			);
			throw error;
		}
	};

	// Update order status
	const updateOrderStatus = async ({
		orderId,
		status,
		trackingNumber,
		carrier,
		notes,
	}: {
		orderId: string;
		status: OrderStatus;
		trackingNumber?: string;
		carrier?: string;
		notes?: string;
	}) => {
		if (!user || !orderId) throw new Error("Missing required information");

		setIsUpdating(true);
		try {
			// Validate tracking number is required for shipped status
			if (status === "shipped" && !trackingNumber) {
				throw new Error(
					"Tracking number is required when marking an order as shipped"
				);
			}

			// First, update the order status in the orders table
			const { data: orderData, error: orderError } = await supabase
				.from("orders")
				.update({ status })
				.eq("id", orderId)
				.select()
				.single();

			if (orderError) throw orderError;

			// Add a new tracking entry
			const { error: trackingError } = await supabase
				.from("order_tracking")
				.insert({
					order_id: orderId,
					status,
					tracking_number: trackingNumber,
					carrier,
					notes,
				});

			if (trackingError) throw trackingError;

			// Get buyer information to send notification
			const { data: orderDetails, error: detailsError } = await supabase
				.from("orders")
				.select("*, buyer:user_id (name, email)")
				.eq("id", orderId)
				.single();

			if (detailsError) {
				console.error("Error fetching order details:", detailsError);
				// Don't throw here, still return the order data
			}

			// Add proper null checks before accessing buyer properties
			if (orderDetails && 
                orderDetails.buyer && 
                typeof orderDetails.buyer === 'object' && 
                'email' in orderDetails.buyer && 
                orderDetails.buyer.email) {
                
				// Send notification to buyer about status update
				const userEmail = orderDetails.buyer.email as string;
				const userName = orderDetails.buyer.name as string;

				try {
					await supabase.functions.invoke("send-order-notification", {
						body: {
							orderId,
							type: "status_update",
							recipientEmail: userEmail,
							recipientName: userName,
							orderDetails,
							trackingInfo: {
								status,
								trackingNumber,
								carrier,
								notes,
							},
						},
					});
				} catch (invokeError) {
					console.error("Error sending notification:", invokeError);
					// Don't throw here, continue with the update
				}
			}

			return orderData;
		} catch (error) {
			console.error("Error updating order status:", error);
			toast({
				title: "Error updating order",
				description: error.message,
				variant: "destructive",
			});
			throw error;
		} finally {
			setIsUpdating(false);
		}
	};

	// Use React Query to manage orders data
	const {
		data: orders = [],
		isLoading,
		error,
		refetch,
	} = useQuery({
		queryKey: ["orders", user?.id, isSupplier],
		queryFn: fetchOrders,
		enabled: !!user,
	});

	// Mutation for updating order status
	const updateStatusMutation = useMutation({
		mutationFn: updateOrderStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["orders"] });
			toast({
				title: "Order updated",
				description: "The order status has been successfully updated.",
			});
		},
	});

	return {
		orders,
		isLoading,
		error,
		refetch,
		fetchOrderById,
		fetchOrderItemsById,
		updateOrderStatus: updateStatusMutation.mutate,
		isUpdating: isUpdating || updateStatusMutation.isPending,
	};
};
